import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Shield, Cpu, TrendingUp, Eye, Star, FileText, Download, ExternalLink } from "lucide-react";

const board = [
  {
    role: "Președinte",
    name: "Aurel Mircea Romocia",
    title: "PhD History",
    icon: Crown,
    testid: "member-presedinte",
    cvs: [
      { label: "CV ENG", href: "/documents/CV_Aurel_Mircea_Romocia_EN.pdf", testid: "cv-en-btn" },
      { label: "CV RO", href: "/documents/CV_Aurel_Mircea_Romocia_RO.pdf", testid: "cv-ro-btn" },
    ],
  },
  {
    role: "Secretar și Trezorier",
    name: "Ing. Romocia Rodica Corina",
    title: "Inginer",
    icon: Shield,
    testid: "member-secretar",
  },
  {
    role: "Informatică",
    name: "Vlad Nichifor",
    title: "Responsabil IT",
    icon: Cpu,
    testid: "member-it",
  },
  {
    role: "Marketing",
    name: "Ec. SASU Cătălin",
    title: "Economist",
    icon: TrendingUp,
    testid: "member-marketing",
  },
  {
    role: "Cenzor",
    name: "Ing. Romocia Flaviu",
    title: "Inginer",
    icon: Eye,
    testid: "member-cenzor",
  },
];

const honorary = [
  { name: "Ambasador Constantin Grigorie", testid: "honor-grigorie" },
  { name: "Drd. Moț Vlad Alexandru", testid: "honor-mot" },
  { name: "Bivol George Cosmin", testid: "honor-bivol" },
];

const AboutUs = () => {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-16 pb-12" data-testid="about-hero">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-soft)] hover:text-[color:var(--gold)]"
          data-testid="about-back-link"
        >
          <ArrowLeft size={14} /> Înapoi la pagina principală
        </Link>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 fade-in">
            <div className="eyebrow" data-testid="about-eyebrow">III. Echipa</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.04]" data-testid="about-title">
              Cine suntem<br />
              <span className="font-display-italic text-[color:var(--gold)]">noi?</span>
            </h1>
          </div>
          <div className="lg:col-span-3 fade-in delay-1 flex justify-center lg:justify-start">
            <img
              src="/assets/logo-asociatie.jpg"
              alt="Sigla Asociației General George Pomuț"
              className="w-[220px] sm:w-[260px] lg:w-full max-w-[280px] h-auto object-contain shadow-[0_18px_40px_-20px_rgba(10,20,40,0.55)]"
              loading="lazy"
              data-testid="about-logo"
            />
          </div>
          <div className="lg:col-span-4 fade-in delay-2">
            <p className="text-[color:var(--ink-soft)] leading-relaxed">
              Asociația este condusă de un consiliu format din cercetători,
              ingineri și profesioniști din Oradea, uniți de aceeași misiune:
              <span className="font-display-italic text-[color:var(--ink)]"> recuperarea
              memoriei generalului George Pomuț.</span>
            </p>
          </div>
        </div>
      </section>

      <div className="rule max-w-[1280px] mx-auto" />

      {/* CONSILIU */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="about-board">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">Consiliul Director</div>
            <h2 className="font-display text-4xl mt-4 leading-tight">
              Membri fondatori<br />
              <span className="font-display-italic text-[color:var(--gold)]">și de conducere.</span>
            </h2>
            <p className="mt-6 text-[color:var(--ink-soft)] text-sm leading-relaxed">
              Echipa care coordonează activitatea curentă a asociației și
              organizează demersurile culturale, științifice și diplomatice.
            </p>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {board.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.role}
                  className={`gold-border bg-[color:var(--cream)]/60 p-8 transition-all duration-500 hover:bg-[color:var(--cream)] hover:-translate-y-1 fade-in delay-${(i % 3) + 1}`}
                  data-testid={m.testid}
                >
                  <div className="flex items-start justify-between">
                    <div className="eyebrow">0{i + 1}</div>
                    <Icon className="text-[color:var(--gold)]" size={22} />
                  </div>
                  <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
                    <div>
                      <div className="font-display-italic text-sm text-[color:var(--gold)] uppercase tracking-[0.18em]">
                        {m.role}
                      </div>
                      <div className="font-display text-2xl mt-2 leading-tight text-[color:var(--ink)]">
                        {m.name}
                      </div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] mt-3">
                        {m.title}
                      </div>
                    </div>
                    {m.cvs && (
                      <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                        {m.cvs.map((cv) => (
                          <a
                            key={cv.label}
                            href={cv.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={cv.testid}
                            className="inline-flex items-center justify-center gap-2 border border-[color:var(--gold)] px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--ink)] transition-colors duration-300 hover:bg-[color:var(--gold)] hover:text-[color:var(--ivory)] whitespace-nowrap"
                          >
                            <FileText size={13} />
                            {cv.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="rule max-w-[1280px] mx-auto" />

      {/* MEMBRI DE ONOARE */}
      <section className="ink-bg mt-0" data-testid="about-honorary">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[color:var(--gold-soft)]">Membri de Onoare</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 text-[color:var(--ivory)] leading-[1.05]">
                Personalități care<br />
                <span className="font-display-italic text-[color:var(--gold-soft)]">sprijină misiunea noastră.</span>
              </h2>
              <p className="text-[color:var(--cream)]/70 mt-6 leading-relaxed text-sm max-w-md">
                Membrii de onoare contribuie la valoarea științifică și culturală
                a demersurilor noastre prin expertiza și prestigiul profesional.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              {honorary.map((h, i) => (
                <div
                  key={h.name}
                  className="border border-white/15 p-8 hover:border-[color:var(--gold-soft)] transition-colors duration-500"
                  data-testid={h.testid}
                >
                  <Star className="text-[color:var(--gold-soft)]" size={22} />
                  <div className="eyebrow text-[color:var(--gold-soft)] mt-6">Membru de Onoare</div>
                  <div className="font-display text-2xl mt-2 text-[color:var(--ivory)] leading-tight">
                    {h.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DATE LEGALE & ISTORIC */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="about-legal">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Date legale</div>
            <h2 className="font-display text-4xl mt-4 leading-tight">
              Identitatea<br />
              <span className="font-display-italic text-[color:var(--gold)]">juridică a asociației.</span>
            </h2>
            <p className="mt-6 text-[color:var(--ink-soft)] leading-relaxed text-sm">
              Asociația operează în baza statutului actualizat și a documentelor
              oficiale emise de instanțele competente din România.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-px">
            <div className="grid grid-cols-12 gap-6 py-5 border-b border-[color:var(--ink)]/10" data-testid="legal-cui">
              <div className="col-span-4 eyebrow self-center">CUI</div>
              <div className="col-span-8 font-display text-2xl text-[color:var(--ink)]">49848993</div>
            </div>

            <div className="grid grid-cols-12 gap-6 py-5 border-b border-[color:var(--ink)]/10" data-testid="legal-former-name">
              <div className="col-span-4 eyebrow self-center">Denumire anterioară</div>
              <div className="col-span-8">
                <div className="font-display text-xl text-[color:var(--ink)] leading-snug">Asociația Filantropică Emanuil Gojdu</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-2 leading-relaxed">
                  Denumirea a fost modificată în anul 2026 în actuala formă:{" "}
                  <span className="text-[color:var(--ink)] font-medium">Asociația General George Pomuț</span>.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 py-5 border-b border-[color:var(--ink)]/10" data-testid="legal-sentinta">
              <div className="col-span-4 eyebrow self-center">Temei juridic</div>
              <div className="col-span-8">
                <div className="font-display text-xl text-[color:var(--ink)]">Sentința Nr. 3901/2026</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-2">Pronunțată în data de 5 mai 2026</div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 py-6" data-testid="legal-document">
              <div className="col-span-4 eyebrow self-center">Document oficial</div>
              <div className="col-span-8">
                <div className="gold-border bg-[color:var(--cream)]/60 p-5 flex items-start gap-4">
                  <FileText className="text-[color:var(--gold)] shrink-0 mt-1" size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-[color:var(--ink)] leading-tight">
                      Sentința civilă Nr. 3901/2026
                    </div>
                    <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                      05 Mai 2026 · PDF
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-4">
                      <a
                        href="/documents/sentinta-3901-2026.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink)] hover:text-[color:var(--gold)] transition-colors"
                        data-testid="legal-doc-view"
                      >
                        <ExternalLink size={14} /> Vizualizează
                      </a>
                      <a
                        href="/documents/sentinta-3901-2026.pdf"
                        download="Sentinta-3901-2026-Asociatia-General-George-Pomut.pdf"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink)] hover:text-[color:var(--gold)] transition-colors"
                        data-testid="legal-doc-download"
                      >
                        <Download size={14} /> Descarcă
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="rule max-w-[1280px] mx-auto" />

      {/* MISIUNE */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="about-mission">
        <figure className="gold-border bg-[color:var(--cream)]/40 p-10 md:p-16">
          <div className="eyebrow">Misiunea noastră</div>
          <blockquote className="font-display-italic text-3xl md:text-4xl leading-snug text-[color:var(--ink)] mt-6">
            „Să fim puntea de legătură între memoria unui român care a făurit
            America de astăzi și conștiința publică din Oradea, din România
            și de pretutindeni.”
          </blockquote>
          <figcaption className="mt-6 eyebrow">— Asociația General George Pomuț</figcaption>
        </figure>
      </section>
    </div>
  );
};

export default AboutUs;
